import { useState, useEffect, useRef } from "react";
import { doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../lib/firebase";

export default function useAutosave(slug, initialData) {
  const [data, setData] = useState(() => {
    const saved = localStorage.getItem(`brief_${slug}`);
    return saved ? JSON.parse(saved) : initialData;
  });
  const [lastSaved, setLastSaved] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const debounceTimer = useRef(null);

  // Sync to localStorage
  useEffect(() => {
    localStorage.setItem(`brief_${slug}`, JSON.stringify(data));
  }, [data, slug]);

  // Sync to Firestore (debounced)
  const saveToFirestore = async (newData, force = false) => {
    if (!slug) return;
    
    setIsSaving(true);
    try {
      await setDoc(doc(db, "briefs", slug), {
        ...newData,
        updatedAt: serverTimestamp(),
        userAgent: navigator.userAgent
      }, { merge: true });
      setLastSaved(new Date());
    } catch (error) {
      console.error("Autosave error:", error);
    } finally {
      setIsSaving(false);
    }
  };

  const updateData = (path, value) => {
    setData(prev => {
      const keys = path.split('.');
      const newData = { ...prev };
      let current = newData;
      
      for (let i = 0; i < keys.length - 1; i++) {
        current[keys[i]] = { ...current[keys[i]] };
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      // Debounce Firestore save
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        saveToFirestore(newData);
      }, 1000);

      return newData;
    });
  };

  const resumeFromFirestore = async () => {
    if (!slug) return null;
    try {
      const docRef = doc(db, "briefs", slug);
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const remoteData = docSnap.data();
        // Convert Firestore timestamps if needed, though we mainly care about answers
        setData(prev => ({ ...prev, ...remoteData }));
        return remoteData;
      }
    } catch (error) {
      console.error("Resume error:", error);
    }
    return null;
  };

  return { data, updateData, isSaving, lastSaved, resumeFromFirestore, saveToFirestore };
}
